import { useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ArrowLeft } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getItem, getList } from "@/lib/api";
import { recordInterest } from "@/lib/interests";
import BookCard from "@/components/BookCard";

const PLATFORMS = [
  { key: "fnac", label: "Acheter sur la Fnac" },
  { key: "cultura", label: "Acheter sur Cultura" },
  { key: "amazon", label: "Acheter sur Amazon" },
  { key: "bookelis", label: "Acheter sur Bookelis" },
];
const STATIC_BOOKS = [
{
id: 1,
title: "Deuil et signe de l’au-delà",
genre: "Deuil",
slug: "deuil-et-signes-de-l-au-dela",
pages: 90,
format: "Broché",
price: "17 €",
summary_long: "Forte de près de vingt années d’expérience dans la médiumnité et l’accompagnement du deuil, Dorothée Lancelot propose des outils simples pour avancer à son rythme, accueillir ses émotions et traverser cette période sans minimiser ce que l’on ressent.",
audience: "Pour les personnes endeuillées et leurs proches.",
themes: ["Deuil", "Émotions", "Signes", "Après", "Accompagnement"],
buy_links: {
fnac: "https://www.fnac.com/a22709299/Dorothee-Lancelot-Deuil-et-signe-de-l-au-dela",
cultura: "https://www.cultura.com/p-deuil-et-signe-de-l-au-dela-9791042483548.html",
bookelis: "https://livres.bookelis.com/sciences-humaines/74800-Deuil-et-signe-de-l-au-dela.html",
}
},

{
id: 2,
title: "Chroniques d’une médium pas comme les autres",
genre: "Médiumnité",
slug: "chroniques-d-une-medium-pas-comme-les-autres",
pages: 178,
format: "Broché",
price: "19 €",
summary_long: "Écrit comme un journal intime ouvert à tous, ce livre mêle médiumnité, contacts avec les défunts, questionnements, amour, bonheur et réflexions sur la vie. Un livre de transmission et de partage, avec le ton direct et personnel de Dorothée Lancelot.",
audience: "Pour les curieux de médiumnité, de spiritualité et de développement personnel.",
themes: ["Médiumnité", "Défunts", "Vie", "Amour", "Réflexion"],
buy_links: {
buy_links: {
cultura: "https://www.cultura.com/p-chroniques-d-une-medium-pas-comme-les-autres-9782379161315.html"
}
}
},

{
id: 3,
title: "Les secrets de la positivité !",
genre: "Feel good",
slug: "les-secrets-de-la-positivite",
pages: 110,
format: "Broché",
price: "10 €",
summary_long: "Un livre consacré aux essentiels de la vie : regarder ce que l’on possède déjà, en prendre conscience, avancer, parfois changer et continuer son chemin. Dorothée Lancelot y partage sa vision de la positivité, de la joie de vivre, du bien-être et du succès intérieur.",
audience: "Pour celles et ceux qui ont besoin d’un coup de boost et d’un regard plus lumineux sur leur quotidien.",
themes: ["Positivité", "Bien-être", "Joie de vivre", "Confiance", "Développement personnel"],
buy_links: {
buy_links: {
fnac: "https://www.fnac.com/a15897679/Dorothee-Lancelot-Les-secrets-de-la-positivite"
}
}
},

{
id: 4,
title: "365 Messages inspirants",
genre: "Spiritualité",
slug: "365-messages-inspirants",
pages: 366,
format: "Broché",
price: "25 €",
summary_long: "365 messages à découvrir au fil de l’année ou simplement en ouvrant une page au hasard. Des mots pensés pour apporter de la force, du courage, de la motivation et parfois le petit déclic dont on avait besoin ce jour-là.",
audience: "Pour toutes celles et ceux qui aiment commencer ou terminer leur journée avec quelques mots inspirants.",
themes: ["Inspiration", "Motivation", "Courage", "Messages", "Quotidien"],
buy_links: {
buy_links: {
fnac: "https://www.fnac.com/a21003207/Dorothee-Lancelot-365-Messages-inspirants"
}
}
},

{
id: 5,
title: "PoéVie",
genre: "Poésie",
slug: "poevie",
pages: 68,
format: "Broché",
summary_long: "Un recueil de poésie qui parle, ressent et aime la vie. Des textes où les émotions, les relations, les blessures, les élans et les contradictions humaines prennent la parole.",
audience: "Pour les lecteurs qui aiment la poésie intime, émotionnelle et profondément humaine.",
themes: ["Poésie", "Vie", "Amour", "Émotions", "Humanité"],
buy_links: {
wook: "https://www.wook.pt/livro/poevie-recueil-de-poesies-parlant-aimant-la-vie-dorothee-lancelot/23154578"
}

},

{
id: 6,
title: "Un autre regard, les mémoires d’une médium",
genre: "Autobiographie",
slug: "un-autre-regard-memoires-d-une-medium",
pages: 156,
format: "Broché",
price: "12 €",
summary_long: "Dorothée Lancelot revient sur son parcours personnel, sa médiumnité, ses ressentis, ses visions, son enfance et les événements qui l’ont construite. Une autobiographie sans chercher à lisser les angles, où se croisent résilience, difficultés, humour, spiritualité et rencontres.",
audience: "Pour celles et ceux qui veulent découvrir la femme derrière la médium et comprendre son parcours.",
themes: ["Autobiographie", "Médiumnité", "Résilience", "Enfance", "Parcours de vie"],
buy_links: {
buy_links: {
fnac: "https://www.fnac.com/a15514127/Dorothee-Lancelot-Un-autre-regard-les-memoires-d-une-medium",
bookelis: "https://livres.bookelis.com/esoterisme/43886-Un-autre-regard-les-memoires-d-une-medium.html"
}
}
},

{
id: 7,
title: "Dream Even",
genre: "Fiction / fantasy",
slug: "dream-even",
pages: 162,
format: "Broché",
price: "16 €",
summary_long: "Dolmen veut fuir sa vie et son passé lorsqu’une panne l’amène dans une ville inconnue : Dream Even. Mais cette ville ne ressemble à aucune autre. Pour survivre, elle devra ne plus rêver ou réaliser les rêves des autres. Un univers fantastique peuplé de personnages cabossés, de secrets et de choix qui ne sont jamais aussi simples qu’ils en ont l’air.",
audience: "Pour les lecteurs de fantasy qui aiment les univers étranges, les personnages imparfaits et les histoires à plusieurs niveaux de lecture.",
themes: ["Fantasy", "Dream Even", "Rêves", "Destin", "Espoir"],
buy_links: {
buy_links: {
fnac: "https://www.fnac.com/a20275369/Dorothee-Lancelot-Dream-Even"
}

}
},

{
id: 8,
title: "ÉsotérismE BooK",
genre: "Ésotérisme",
slug: "esoterisme-book",
pages: 98,
format: "Broché",
price: "17 €",
summary_long: "Un ouvrage pour apprendre, comprendre et pratiquer l’ésotérisme. Dorothée Lancelot y aborde l’histoire des pratiques, les outils, les méthodologies, le mysticisme, l’au-delà et les différents univers qui entourent l’ésotérisme.",
audience: "Pour les curieux comme pour ceux qui souhaitent mieux comprendre les pratiques ésotériques avant de les utiliser.",
themes: ["Ésotérisme", "Histoire", "Pratiques", "Mysticisme", "Apprentissage"],
buy_links: {
buy_links: {
fnac: "https://www.fnac.com/a17413476/Dorothee-Lancelot-EsoterismE-BooK"
}
}
}
];
export default function BookDetail() {
  const { slug } = useParams();
  const book = STATIC_BOOKS.find((b) => b.slug === slug);
const all = STATIC_BOOKS;
const isLoading = false;
const isError = !book;

  const related = useMemo(() => all.filter((b) => b.slug !== slug).slice(0, 3), [all, slug]);

  useEffect(() => { if (book?.genre) recordInterest(book.genre); }, [book]);

  useSeo({
    title: book?.title,
    description: book ? `${book.title} de Dorothée Lancelot${book.genre ? " — " + book.genre : ""}. ${book.summary_short || ""}`.slice(0, 300) : "Livre de Dorothée Lancelot",
    path: `/livres/${slug}`,
    jsonLd: book ? { "@context": "https://schema.org", "@type": "Book", name: book.title, author: { "@type": "Person", name: "Dorothée Lancelot" }, genre: book.genre || undefined } : undefined,
  });

  if (isLoading) return <div className="dl-container py-40 text-center text-[#F3E9D2]/50">Chargement…</div>;
  if (isError || !book) return <div className="dl-container py-40 text-center text-[#F3E9D2]/60">Livre introuvable. <Link className="dl-link" to="/livres">Retour aux livres</Link></div>;

  const buy = book.buy_links || {};
  const availableLinks = PLATFORMS.filter((p) => buy[p.key]);

  return (
    <div>
      <section className="dl-container pt-12 pb-6">
        <Link to="/livres" className="inline-flex items-center gap-2 dl-eyebrow hover:text-[#F3E9D2]"><ArrowLeft size={14} /> Tous les livres</Link>
      </section>

      <section className="dl-container pb-20 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-14">
        {/* Cover */}
        <div>
          <div className="dl-book">
            <div className="dl-book-cover aspect-[2/3] bg-[#0B1020] border border-[rgba(198,167,106,0.3)] overflow-hidden sticky top-24">
              {book.cover_url ? (
                <img src={book.cover_url} alt={`Couverture — ${book.title}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center" style={{ background: "linear-gradient(160deg, #141A2E, #0B1020)" }}>
                  <BookOpen size={38} className="text-[#C6A76A]/60 mb-5" />
                  <span className="serif text-2xl text-[#F3E9D2]">{book.title}</span>
                  <span className="dl-eyebrow mt-4" style={{ fontSize: "0.6rem" }}>Dorothée Lancelot</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Details */}
        <div>
          {book.genre && <div className="dl-eyebrow mb-4">{book.genre}</div>}
          <h1 className="serif text-4xl md:text-6xl text-[#F3E9D2] leading-tight">{book.title}</h1>
          <p className="text-[#D4B978] mt-3 tracking-wide">Dorothée Lancelot</p>

          <div className="flex flex-wrap gap-x-10 gap-y-2 mt-6 text-sm text-[#F3E9D2]/60">
            {book.pages && <span><b className="text-[#F3E9D2]/80">Pages :</b> {book.pages}</span>}
            {book.format && <span><b className="text-[#F3E9D2]/80">Format :</b> {book.format}</span>}
            {book.price && <span className="text-[#D4B978] text-base font-medium">{book.price}</span>}
          </div>

          {book.summary_long ? (
            <p className="mt-8 text-[#F3E9D2]/80 leading-relaxed whitespace-pre-line">{book.summary_long}</p>
          ) : book.summary_short ? (
            <p className="mt-8 text-[#F3E9D2]/80 leading-relaxed">{book.summary_short}</p>
          ) : null}

          {book.excerpt && (
            <div className="mt-8 border-l-2 border-[#C6A76A] pl-6">
              <div className="dl-eyebrow mb-2" style={{ fontSize: "0.6rem" }}>Extrait</div>
              <p className="serif italic text-lg text-[#F3E9D2]/85 whitespace-pre-line">{book.excerpt}</p>
            </div>
          )}

          {book.audience && (
            <div className="mt-8">
              <div className="dl-eyebrow mb-2" style={{ fontSize: "0.6rem" }}>Pour qui est ce livre ?</div>
              <p className="text-[#F3E9D2]/75">{book.audience}</p>
            </div>
          )}

          {Array.isArray(book.themes) && book.themes.length > 0 && (
            <div className="mt-8">
              <div className="dl-eyebrow mb-3" style={{ fontSize: "0.6rem" }}>Thèmes abordés</div>
              <div className="flex flex-wrap gap-2">
                {book.themes.map((t, i) => <span key={i} className="px-3 py-1.5 text-xs border border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70">{t}</span>)}
              </div>
            </div>
          )}

          {/* Buy */}
          {availableLinks.length > 0 && (
            <div className="mt-10 pt-8 border-t border-[rgba(198,167,106,0.2)]">
              <div className="dl-eyebrow mb-4">Se procurer le livre</div>
              <div className="flex flex-wrap gap-3">
                {availableLinks.map((p) => (
                  <a key={p.key} href={buy[p.key]} target="_blank" rel="noreferrer" data-testid={`buy-${p.key}`} className="dl-btn dl-btn-solid">{p.label}</a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="dl-container py-16 border-t border-[rgba(198,167,106,0.15)]">
          <h2 className="serif text-3xl text-[#F3E9D2] mb-10">Vous aimerez peut-être aussi</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
            {related.map((b) => <BookCard key={b.id} book={b} />)}
          </div>
        </section>
      )}
    </div>
  );
}
